import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Search, ShoppingBag } from 'lucide-react';
import { kotPrinters, receiptPrinter } from '@shripos/shared/escpos';
import { OFFLINE_REASONS } from '@shripos/shared/offline';
import { Alert, Button, Dialog, Input, PageLoader, useToast } from '@shripos/ui';
import { api } from '../../lib/api.js';
import { itemAvailableNow, useCatalog, useCatalogMaps, usePricedCart } from '../../lib/catalog.js';
import { rupees } from '../../lib/money.js';
import { completeOffline, flushOutbox, refreshCounts } from '../../lib/offline/index.js';
import { holdOffline, holdRow, holdWaiting, refreshHolds } from '../../lib/offline/holds.js';
import { kitchenTicketsFor, sendToLocalKitchen } from '../../lib/offline/kitchen.js';
import { withOverride } from '../../lib/override.js';
import { usePrinters, usePrinting } from '../../lib/printing/usePrinting.js';
import { cartToBillInput, useCart } from '../../stores/cart.js';
import { isNetworkError, useOffline } from '../../stores/offline.js';
import { kitchenOf, useSession } from '../../stores/session.js';
import { CartPanel } from './CartPanel.jsx';
import { HeldOrdersDialog, useHeldOrders } from './HeldOrdersDialog.jsx';
import { ItemOptionsDialog } from './ItemOptionsDialog.jsx';
import { CategoryRail, ItemTile } from './MenuGrid.jsx';
import { PaymentDialog } from './PaymentDialog.jsx';
import { OpenShiftDialog, ShiftChip, useShift } from '../shifts/ShiftWidgets.jsx';
import { PrinterChip } from './PrinterChip.jsx';
import { SaleDoneDialog } from './ReceiptActions.jsx';
import { CustomerDialog, LineDialog, OffersDialog, ReasonDialog } from './SmallDialogs.jsx';

function useMinuteTick() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function PosPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const me = useSession((s) => s.me);
  const catalogQ = useCatalog(Boolean(me.outlet));
  const catalog = catalogQ.data;
  // Kitchen orders at this outlet (plan + the outlet's switch). Without them: no KOT step at all.
  const kitchenOrders = (catalog?.kitchen ?? kitchenOf(me)).orders;
  const maps = useCatalogMaps(catalog);
  const cart = useCart((s) => s.cart);
  const printing = usePrinting();
  const printersQ = usePrinters();
  const navigate = useNavigate();
  const shiftQ = useShift(Boolean(me.outlet));
  // Billing needs an open shift (setting): ask for the opening cash straight away.
  const [shiftPromptDismissed, setShiftPromptDismissed] = useState(false);
  // The shift chip's own dialogs (e.g. "Shift #1 closed"): the next shift's prompt waits for them.
  const [chipDialogOpen, setChipDialogOpen] = useState(false);
  const needShift = Boolean(shiftQ.data && !shiftQ.data.shift && shiftQ.data.settings?.requireOpenShift);
  const ensureOutlet = useCart((s) => s.ensureOutlet);
  const addLine = useCart((s) => s.addLine);
  const reset = useCart((s) => s.reset);
  const loadBill = useCart((s) => s.loadBill);
  const couponQ = useQuery({
    queryKey: ['coupon', cart.couponCode],
    queryFn: () => api.get(`/pos/coupons/${encodeURIComponent(cart.couponCode)}`),
    enabled: Boolean(cart.couponCode),
    retry: false,
  });
  const coupon = cart.couponCode && couponQ.data ? couponQ.data : null;
  const priced = usePricedCart(cart, catalog, maps, coupon);
  const reachable = useOffline((s) => s.reachable);
  // Signed in with a PIN checked on this device: work stays offline until the real sign-in.
  const offlineSession = useSession((s) => s.offline);
  const held = useHeldOrders();
  const now = useMinuteTick();
  const [category, setCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  // Orders placed on a self-order kiosk land in "Open orders": tell the cashier.
  useEffect(() => {
    const onOrder = (e) =>
      toast.info(
        e.detail?.payWith === 'upi'
          ? t('pos.selfOrderUpi', 'Self-order · Token {{token}} (check the UPI payment)', {
              token: e.detail?.tokenNo ?? '',
            })
          : t('pos.selfOrderCounter', 'Self-order · Token {{token}} (pays here)', { token: e.detail?.tokenNo ?? '' }),
        { action: { label: t('pos.openOrders', 'Open orders'), onClick: () => setDialog('held') } },
      );
    window.addEventListener('shripos:selforder', onOrder);
    return () => window.removeEventListener('shripos:selforder', onOrder);
  }, [toast, t]);
  const [busy, setBusy] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (me.outlet) ensureOutlet(me.outlet.id);
  }, [me.outlet, ensureOutlet]);

  // Keep this device's copy of the open orders fresh, so they can be resumed offline.
  useEffect(() => {
    if (reachable && me.device) void refreshHolds();
  }, [reachable, me.device]);

  const hasFavorites = Boolean(catalog?.items.some((i) => i.isFavorite));
  const activeCategory = category ?? (hasFavorites ? 'favorites' : (catalog?.categories[0]?.id ?? 'all'));

  const visible = useMemo(() => {
    if (!catalog) return [];
    const q = search.trim().toLowerCase();
    let items = catalog.items;
    if (q)
      items = items.filter(
        (i) =>
          i.name.toLowerCase().includes(q) || i.shortCode?.toLowerCase() === q || i.barcodes.includes(search.trim()),
      );
    else if (activeCategory === 'favorites') items = items.filter((i) => i.isFavorite);
    else if (activeCategory !== 'all') items = items.filter((i) => i.categoryId === activeCategory);
    return items.map((i) => {
      const inTime = itemAvailableNow(i, maps, catalog.outlet.timezone, now);
      const allVariantsOut = i.variants.length > 0 && i.variants.every((v) => v.outOfStock);
      const unavailable = i.outOfStock || allVariantsOut || !inTime;
      return {
        item: i,
        unavailable,
        reason: !inTime ? t('pos.notNow', 'Not now') : t('pos.outOfStock', 'Out of stock'),
      };
    });
  }, [catalog, maps, search, activeCategory, now, t]);

  const onAdd = (item) => {
    if (item.variants.length || item.modifierGroupIds.length) setDialog({ type: 'options', item });
    else addLine({ itemId: item.id });
  };

  /** Barcode / short code lookup (keyboard-wedge scanners type the code and press Enter). */
  const addByCode = (code) => {
    if (!catalog) return false;
    const c = code.trim();
    for (const i of catalog.items) {
      const v = i.variants.find((x) => x.barcode === c);
      if (i.barcodes.includes(c) || v || i.shortCode === c.toUpperCase()) {
        if (i.outOfStock || v?.outOfStock) {
          toast.error(t('pos.itemOutOfStock', '{{name}} is out of stock', { name: i.name }));
          return true;
        }
        if (v && !i.modifierGroupIds.length) addLine({ itemId: i.id, variantId: v.id });
        else onAdd(i);
        return true;
      }
    }
    return false;
  };

  /**
   * Thermal printing after a save (Settings → Receipt & printing). Prints run in the background;
   * failures show a toast with "Print in browser". Returns KOTs no kitchen printer handles.
   */
  const autoPrintKots = (kots) => {
    if (!kitchenOrders) return [];
    const printers = printersQ.data ?? [];
    const routed = catalog.settings.printing?.autoPrintKot
      ? (kots ?? []).filter((k) => kotPrinters(printers, k.station).length)
      : [];
    if (routed.length) printing.kots(routed, { auto: true });
    return (kots ?? []).filter((k) => !routed.includes(k));
  };

  const autoPrintSale = (bill, payments, model = null) => {
    const p = catalog.settings.printing ?? {};
    const auto = Boolean(p.autoPrintReceipt && receiptPrinter(printersQ.data ?? []));
    const cash = (payments ?? []).some((x) => x.mode === 'cash');
    if (auto)
      printing.receipt(bill.id, { reprint: false, auto: true, openDrawer: Boolean(p.openDrawerOnCash && cash), model });
    if (catalog.settings.tokenSlip?.enabled) printing.tokenSlip(bill, { auto: true });
    return auto;
  };

  /** Kitchen tickets for work done offline: printed here, and on local kitchen screens if enabled. */
  const offlineKitchen = (bill, { includeNew = true } = {}) => {
    if (!kitchenOrders) return [];
    const tickets = kitchenTicketsFor({
      cartLines: cart.lines,
      sentQty: cart.sentQty,
      sentLines: cart.sentLines,
      bill,
      catalog,
      me,
      includeNew,
    });
    if (catalog.settings.offline?.localKitchen) void sendToLocalKitchen(tickets);
    return autoPrintKots(tickets);
  };

  /**
   * No internet on a registered device: complete the sale from the device's own series, print
   * locally (receipt and kitchen tickets) and sync later (Settings → Offline & displays).
   */
  const saveOffline = async (payments) => {
    try {
      const r = await completeOffline({ cart, priced, catalog, payments, shiftId: shiftQ.data?.shift?.id ?? null });
      const leftover = offlineKitchen(r.bill);
      reset();
      const autoPrinted = autoPrintSale(r.bill, payments, r.receipt);
      qc.invalidateQueries({ queryKey: ['bills', 'held'] });
      setDialog({ type: 'done', bill: r.bill, kots: leftover, autoPrinted, receipt: r.receipt });
    } catch (err) {
      toast.error(
        err.offlineReason
          ? t(`offline.reason.${err.offlineReason}`, {
              defaultValue: OFFLINE_REASONS[err.offlineReason] ?? err.message,
            })
          : err.message,
      );
    }
  };

  /** Hold, or send to the kitchen, without internet (kept on this device until it syncs). */
  const holdOfflineNow = async (action) => {
    try {
      const r = await holdOffline({ cart, priced, catalog, action });
      await refreshCounts(); // "N to sync" in the top bar
      const leftover = offlineKitchen(r.bill, { includeNew: action === 'kot' });
      reset();
      qc.invalidateQueries({ queryKey: ['bills', 'held'] });
      setDialog(leftover.length ? { type: 'kot', kots: leftover, bill: r.bill } : null);
      toast.success(
        action === 'kot'
          ? t('pos.sentOffline', 'Sent to kitchen · Token {{token}} (saved on this device)', { token: r.bill.tokenNo })
          : t('pos.heldOffline', 'Order saved on this device. It syncs when the internet is back.'),
      );
    } catch (err) {
      toast.error(
        err.offlineReason
          ? t(`offline.reason.${err.offlineReason}`, {
              defaultValue: OFFLINE_REASONS[err.offlineReason] ?? err.message,
            })
          : err.message,
      );
    }
  };

  const save = async (action, payments) => {
    if (!priced || (action === 'kot' && !kitchenOrders)) return;
    const deviceCanWorkOffline = Boolean(me.device);
    if ((!useOffline.getState().reachable || offlineSession) && deviceCanWorkOffline) {
      setBusy(true);
      if (action === 'complete') await saveOffline(payments);
      else await holdOfflineNow(action);
      setBusy(false);
      return;
    }
    setBusy(true);
    try {
      // Saved offline earlier and not uploaded yet: upload first, so the kitchen doesn't get it twice.
      if (await holdWaiting(cart.clientUuid)) {
        await flushOutbox();
        const still = await holdWaiting(cart.clientUuid);
        if (still) {
          toast.error(
            still.status === 'failed'
              ? t('pos.syncRefused', 'This order could not sync: {{reason}}. See the sync status at the top.', {
                  reason: still.error?.message ?? t('pos.refused', 'refused'),
                })
              : t('pos.stillSyncing', 'This order is still syncing. Try again in a moment.'),
          );
          return;
        }
      }
      const body = cartToBillInput(cart, action, { payments, expectedTotalPaise: priced.totals.grandTotalPaise });
      const res = await withOverride((headers) => api.post('/bills', body, { headers }));
      qc.invalidateQueries({ queryKey: ['bills'] });
      qc.invalidateQueries({ queryKey: ['today'] });
      reset();
      if (action === 'complete') {
        const leftover = autoPrintKots(res.kots);
        const autoPrinted = autoPrintSale(res.bill, payments);
        setDialog({ type: 'done', bill: res.bill, kots: leftover, autoPrinted });
      } else {
        const leftover = autoPrintKots(res.kots);
        setDialog(leftover.length ? { type: 'kot', kots: leftover, bill: res.bill } : null);
        toast.success(
          action === 'kot'
            ? t('pos.sentToKitchen', 'Sent to kitchen · Token {{token}}', { token: res.bill.tokenNo })
            : t('pos.held', 'Order saved to open orders'),
        );
      }
    } catch (err) {
      if (err.cancelled) return;
      if (isNetworkError(err) && me.device) {
        useOffline.getState().set({ reachable: false });
        if (action === 'complete') await saveOffline(payments);
        else await holdOfflineNow(action);
        return;
      }
      if (err.code === 'SHIFT_REQUIRED') {
        setShiftPromptDismissed(false);
        await qc.invalidateQueries({ queryKey: ['shift-current'] });
        setDialog('open-shift');
        return;
      }
      if (err.code === 'DAY_NOT_CLOSED') {
        toast.error(
          err.message,
          shiftQ.data?.canManage
            ? { action: { label: t('pos.closeTheDay', 'Close the day'), onClick: () => navigate('/cash') } }
            : {},
        );
        return;
      }
      if (err.code === 'PRICE_CHANGED') {
        await qc.invalidateQueries({ queryKey: ['catalog'] });
        setDialog(null);
        toast.info(t('pos.pricesChanged', 'Prices changed since this order started. Please check the new total.'));
      } else toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  /** Open an order from the list: from the server, or from this device's copy when offline. */
  const resume = async (order) => {
    if (cart.lines.length && cart.clientUuid !== order.clientUuid) {
      toast.error(t('pos.holdFirst', 'Hold or clear the current order first.'));
      return;
    }
    try {
      const online = useOffline.getState().reachable && !offlineSession;
      if (online && order.local) await flushOutbox(); // upload this device's offline save first
      const local = await holdRow(order.clientUuid);
      if (online && !(await holdWaiting(order.clientUuid))) {
        const id = local?.serverId ?? order.id;
        loadBill(await api.get(`/bills/${id}`));
      } else if (local) {
        loadBill(local);
      } else {
        toast.error(t('pos.notOnDevice', 'This order is not on this device. Open it again when the internet is back.'));
        return;
      }
      setDialog(null);
    } catch (err) {
      if (isNetworkError(err)) {
        useOffline.getState().set({ reachable: false });
        const local = await holdRow(order.clientUuid);
        if (local) {
          loadBill(local);
          setDialog(null);
          return;
        }
      }
      toast.error(err);
    }
  };

  const cancelOrder = async (reason) => {
    if (!useOffline.getState().reachable || offlineSession || cart.billId?.startsWith('local:')) {
      toast.error(
        t(
          'pos.cancelNeedsInternet',
          'Cancelling an order needs an internet connection (it may need a manager and is recorded).',
        ),
      );
      setDialog(null);
      return;
    }
    const res = await withOverride((headers) => api.post(`/bills/${cart.billId}/cancel`, { reason }, { headers }));
    reset();
    qc.invalidateQueries({ queryKey: ['bills'] });
    toast.success(t('pos.orderCancelled', 'Order cancelled'));
    const leftover = autoPrintKots(res.kots);
    if (leftover.length) setDialog({ type: 'kot', kots: leftover, bill: res.bill });
  };

  // Keyboard shortcuts and barcode scanner (only when no dialog is open).
  const keys = useRef({});
  useEffect(() => {
    keys.current = {
      pay: () => cart.lines.length && !busy && setDialog('pay'),
      hold: () => cart.lines.length && !busy && save('hold'),
      kot: () => kitchenOrders && cart.lines.length && !busy && save('kot'),
      newOrder: () => !cart.billId && reset(),
      code: addByCode,
      blocked: Boolean(dialog),
    };
  });
  useEffect(() => {
    let buffer = '';
    let last = 0;
    const onKey = (e) => {
      const k = keys.current;
      if (k.blocked) return;
      const shortcuts = { F2: () => searchRef.current?.focus(), F4: k.pay, F6: k.hold, F8: k.kot, F9: k.newOrder };
      if (shortcuts[e.key]) {
        e.preventDefault();
        shortcuts[e.key]();
        return;
      }
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      const tNow = performance.now();
      if (e.key === 'Enter') {
        if (buffer.length >= 3 && !k.code(buffer))
          toast.error(t('pos.noItemCode', 'No item with code {{code}}', { code: buffer }));
        buffer = '';
        return;
      }
      if (e.key.length === 1) {
        if (tNow - last > 80) buffer = '';
        buffer += e.key;
        last = tNow;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toast, t]);

  if (!me.outlet) return <Alert tone="warning">{t('pos.chooseOutlet', 'Choose an outlet first.')}</Alert>;
  if (catalogQ.isLoading) return <PageLoader label={t('pos.loadingMenu', 'Loading menu…')} />;
  if (catalogQ.error) return <Alert tone="danger">{catalogQ.error.message}</Alert>;
  if (!catalog.items.length) {
    return (
      <Alert tone="info" title={t('pos.menuEmpty', 'The menu is empty')}>
        {t('pos.menuEmptyBody', 'Add categories, tax groups and items under Menu before billing.')}
      </Alert>
    );
  }

  const lineMeta = (lineId) => priced?.byId.get(lineId)?.meta;
  const cartPanel = (
    <CartPanel
      catalog={catalog}
      priced={priced}
      busy={busy}
      onCustomer={() => setDialog('customer')}
      onLine={(line) => setDialog({ type: 'line', line })}
      onDiscount={() => setDialog('offers')}
      onHold={() => save('hold')}
      onKot={kitchenOrders ? () => save('kot') : null}
      onPay={() => setDialog('pay')}
      onCancelOrder={() => setDialog('cancel')}
    />
  );

  return (
    <div className="flex h-full min-h-0">
      {/* Menu */}
      <div className="flex min-w-0 flex-1 flex-col md:flex-row">
        <div className="shrink-0 border-slate-200 bg-stone-50 p-2 md:w-44 md:overflow-y-auto md:border-r lg:w-52">
          <CategoryRail
            categories={catalog.categories}
            active={activeCategory}
            hasFavorites={hasFavorites}
            onSelect={(c) => (setCategory(c), setSearch(''))}
          />
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2 border-b border-slate-200 bg-white p-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
              <Input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (
                      addByCode(search) ||
                      (visible.length === 1 && !visible[0].unavailable && (onAdd(visible[0].item), true))
                    )
                      setSearch('');
                  }
                  if (e.key === 'Escape') setSearch('');
                }}
                placeholder={t('pos.search', 'Search, code or scan (F2)')}
                className="pl-9"
                aria-label="Search items"
              />
            </div>
            <ShiftChip shiftQuery={shiftQ} onOpen={() => setDialog('open-shift')} onDialogChange={setChipDialogOpen} />
            <PrinterChip />
            <Button variant="secondary" icon={Clock} onClick={() => setDialog('held')}>
              <span className="hidden sm:inline">{t('pos.openOrders', 'Open orders')}</span>
              {held.data?.total > 0 && (
                <span className="rounded-full bg-brand-600 px-1.5 text-xs text-white">{held.data.total}</span>
              )}
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2 pb-24 md:pb-2">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
              {visible.map(({ item, unavailable, reason }) => (
                <ItemTile
                  key={item.id}
                  item={item}
                  unavailable={unavailable}
                  reason={reason}
                  onAdd={onAdd}
                  showImages
                />
              ))}
            </div>
            {visible.length === 0 && (
              <p className="p-6 text-center text-sm text-slate-500">{t('pos.noMatch', 'No items match.')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Cart: side panel on tablets/desktops, bottom sheet on phones */}
      <aside className="hidden w-[22rem] shrink-0 border-l border-slate-200 md:block lg:w-96">{cartPanel}</aside>
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:hidden">
        <Button
          size="lg"
          className="w-full justify-between"
          icon={ShoppingBag}
          onClick={() => setDialog('cart')}
          disabled={!cart.lines.length}
        >
          <span>
            {t('pos.viewOrder', 'View order')} ({priced?.totals.itemCount ?? 0})
          </span>
          <span>{rupees(priced?.totals.grandTotalPaise ?? 0)}</span>
        </Button>
      </div>

      {dialog === 'cart' && (
        <Dialog open onClose={() => setDialog(null)} title={t('pos.order', 'Order')} size="md">
          <div className="-mx-5 -my-4 h-[75vh]">{cartPanel}</div>
        </Dialog>
      )}
      {dialog?.type === 'options' && (
        <ItemOptionsDialog item={dialog.item} maps={maps} onAdd={addLine} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === 'line' && (
        <LineDialog
          line={dialog.line}
          meta={lineMeta(dialog.line.lineId)}
          limits={me.user.limits}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'customer' && <CustomerDialog onClose={() => setDialog(null)} />}
      {dialog === 'offers' && (
        <OffersDialog limits={me.user.limits} onCoupon={() => {}} onClose={() => setDialog(null)} />
      )}
      {dialog === 'held' && <HeldOrdersDialog onResume={resume} onClose={() => setDialog(null)} />}
      {dialog === 'cancel' && (
        <ReasonDialog
          title={t('pos.cancelTitle', 'Cancel this order?')}
          description={
            kitchenOrders
              ? t(
                  'pos.cancelBody',
                  'Items already sent to the kitchen are cancelled with a ticket. Needs manager approval if food was sent.',
                )
              : t('pos.cancelBodyNoKitchen', 'The order is removed from open orders. The reason is recorded.')
          }
          confirmLabel={t('pos.cancelOrder', 'Cancel order')}
          onConfirm={cancelOrder}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'pay' && priced && (
        <PaymentDialog
          totalPaise={priced.totals.grandTotalPaise}
          upi={catalog.outlet.pos.upi}
          hasCustomer={Boolean(cart.customer)}
          customer={cart.customer}
          loyalty={me.license?.features?.crm && catalog.settings.loyalty?.enabled ? catalog.settings.loyalty : null}
          offline={reachable ? null : catalog.settings.offline}
          reference={cart.tokenNo ? `Token ${cart.tokenNo}` : `${catalog.outlet.code} order`}
          busy={busy}
          onComplete={(payments) => save('complete', payments)}
          onClose={() => setDialog(null)}
        />
      )}
      {(dialog === 'open-shift' || (needShift && !shiftPromptDismissed && !dialog && !chipDialogOpen)) && (
        <OpenShiftDialog
          onClose={() => {
            setShiftPromptDismissed(true);
            if (dialog === 'open-shift') setDialog(null);
          }}
        />
      )}
      {dialog?.type === 'done' && (
        <SaleDoneDialog
          bill={dialog.bill}
          kots={dialog.kots}
          autoPrinted={dialog.autoPrinted}
          offlineReceipt={dialog.receipt ?? null}
          onNext={() => {
            setDialog(null);
            searchRef.current?.focus();
          }}
        />
      )}
      {dialog?.type === 'kot' && (
        <Dialog
          open
          onClose={() => setDialog(null)}
          title={t('pos.kitchenTickets', 'Kitchen tickets · Token {{token}}', { token: dialog.bill.tokenNo ?? '—' })}
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setDialog(null)}>
                {t('pos.skip', 'Skip')}
              </Button>
              <Button
                onClick={async () => {
                  await printing.kots(dialog.kots);
                  setDialog(null);
                }}
              >
                {t('pos.printKots', 'Print {{count}} KOTs', { count: dialog.kots.length })}
              </Button>
            </>
          }
        >
          <ul className="space-y-1 text-sm">
            {dialog.kots.map((k) => (
              <li key={k.id}>
                {t('pos.kot', 'KOT')} #{k.kotNo} · {k.station}{' '}
                {k.type === 'cancel' ? t('pos.cancellation', '(cancellation)') : ''} ·{' '}
                {t('pos.itemCount', '{{count}} item(s)', { count: k.lines.reduce((s, l) => s + l.qty, 0) })}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            {t('pos.addKitchenPrinters', 'Add kitchen printers under Printers and these print by themselves.')}
          </p>
        </Dialog>
      )}
    </div>
  );
}
